// Mis procedimientos (MiPAE extendido, Fase 3): procedimientos PROPIOS del
// usuario en la sección Procedimientos de la Enciclopedia, con la MISMA forma
// de datos que los de la casa (src/data/referencia/procedimientos.js:
// {id, titulo, objetivo, materiales, pasos, clave}) para reusar el mismo render.
//
// Es ayuda memoria de TÉCNICA, jamás de pacientes: todo texto libre pasa por la
// guarda DURA de PII (detectPiiDuro, la misma de las notas del turno) antes de
// guardarse, y se RE-VALIDA al leer (un jsonb adulterado con un nombre adentro
// no se muestra). Se persiste en profiles.procedimientos_propios (jsonb array,
// migración 20260922100000), bajo la RLS propia del perfil.
//
// Tope por plan: 3 en gratis (para probar), 20 en MiPAE extendido. El tope lo
// aplica quien ESCRIBE; la lectura solo acota al máximo absoluto, así a quien
// se le venció el plan no se le borra nada al leer.

import { supabase } from './supabase';
import { detectPiiDuro } from './piiDetect';

export const MAX_PROPIOS_GRATIS = 3;
export const MAX_PROPIOS_EXTENDIDA = 20;
export const MAX_TITULO = 80;
export const MAX_LINEA = 200;
export const MAX_ITEMS = { materiales: 20, pasos: 30, clave: 10 };

// Kill-switch (ON por defecto, regla del repo: `!== 'false'`).
export function procedimientosPropiosHabilitado() {
  return process.env.REACT_APP_PROCEDIMIENTOS_PROPIOS_ENABLED !== 'false';
}

export function maxProcedimientosPara(plus) {
  return plus ? MAX_PROPIOS_EXTENDIDA : MAX_PROPIOS_GRATIS;
}

function linea(v, max = MAX_LINEA) {
  return String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, max);
}

// Lista de líneas: acepta array o texto con una línea por ítem (el editor).
export function aLineas(v, max) {
  const arr = Array.isArray(v) ? v : String(v == null ? '' : v).split('\n');
  return arr.map((x) => linea(x)).filter(Boolean).slice(0, max);
}

// Todos los textos de un procedimiento, para pasarlos por la guarda.
function textosDe(p) {
  return [p.titulo, p.objetivo, ...(p.materiales || []), ...(p.pasos || []), ...(p.clave || [])];
}

// Guarda DURA de PII sobre un procedimiento ya normalizado. Devuelve los
// matches (vacío = limpio).
export function piiEnProcedimiento(p) {
  const out = [];
  for (const t of textosDe(p)) {
    if (!t) continue;
    const { matches } = detectPiiDuro(t);
    out.push(...matches);
  }
  return out;
}

const ID_RE = /^[A-Za-z0-9_-]{1,40}$/;

export function nuevoId(ahora = Date.now()) {
  return `mio-${ahora.toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

// Normaliza UN procedimiento crudo (del editor o de la BD). null si no sirve
// (sin título, sin pasos, id inválido o con PII dura en algún texto).
export function normalizarProcedimiento(raw, { ahora = Date.now() } = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const titulo = linea(raw.titulo, MAX_TITULO);
  if (!titulo) return null;
  const p = {
    id: ID_RE.test(String(raw.id || '')) ? String(raw.id) : nuevoId(ahora),
    titulo,
    objetivo: linea(raw.objetivo),
    materiales: aLineas(raw.materiales, MAX_ITEMS.materiales),
    pasos: aLineas(raw.pasos, MAX_ITEMS.pasos),
    clave: aLineas(raw.clave, MAX_ITEMS.clave),
    propio: true,
  };
  if (!p.pasos.length) return null;
  if (piiEnProcedimiento(p).length) return null;
  return p;
}

// Valor crudo de la BD → lista limpia (dedupe por id, tope absoluto).
export function normalizarProcedimientos(valor) {
  if (!Array.isArray(valor)) return [];
  const out = [];
  const ids = new Set();
  for (const raw of valor) {
    const p = normalizarProcedimiento(raw);
    if (!p || ids.has(p.id)) continue;
    ids.add(p.id);
    out.push(p);
    if (out.length >= MAX_PROPIOS_EXTENDIDA) break;
  }
  return out;
}

// Decide si un procedimiento cabe (puro, testeable): devuelve { ok, motivo?, p? }.
// Motivos de catálogo cerrado: 'deshabilitado' | 'invalido' | 'pii' | 'tope'.
export function evaluarGuardado(lista, raw, { plus = false } = {}) {
  if (!procedimientosPropiosHabilitado()) return { ok: false, motivo: 'deshabilitado' };
  if (!raw || typeof raw !== 'object' || !linea(raw.titulo)) return { ok: false, motivo: 'invalido' };
  const candidato = {
    titulo: linea(raw.titulo, MAX_TITULO), objetivo: linea(raw.objetivo),
    materiales: aLineas(raw.materiales, MAX_ITEMS.materiales), pasos: aLineas(raw.pasos, MAX_ITEMS.pasos), clave: aLineas(raw.clave, MAX_ITEMS.clave),
  };
  if (!candidato.pasos.length) return { ok: false, motivo: 'invalido' };
  const matches = piiEnProcedimiento(candidato);
  if (matches.length) return { ok: false, motivo: 'pii', matches };
  const p = normalizarProcedimiento(raw);
  if (!p) return { ok: false, motivo: 'invalido' };
  const existe = lista.some((x) => x.id === p.id);
  if (!existe && lista.length >= maxProcedimientosPara(plus)) return { ok: false, motivo: 'tope' };
  return { ok: true, p };
}

export async function cargarProcedimientosPropios(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('procedimientos_propios')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return [];
    return normalizarProcedimientos(data?.procedimientos_propios);
  } catch {
    return [];
  }
}

export async function guardarProcedimientosPropios(userId, lista) {
  if (!userId) return false;
  const limpio = normalizarProcedimientos(lista).map(({ propio, ...p }) => p);
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ procedimientos_propios: limpio })
      .eq('user_id', userId);
    return !error;
  } catch {
    return false;
  }
}

// Agrega o actualiza (por id). Devuelve { ok, motivo?, matches?, lista }.
export async function upsertProcedimientoPropio(userId, raw, { plus = false } = {}) {
  const actual = await cargarProcedimientosPropios(userId);
  const ev = evaluarGuardado(actual, raw, { plus });
  if (!ev.ok) return { ...ev, lista: actual };
  const idx = actual.findIndex((x) => x.id === ev.p.id);
  const lista = idx >= 0 ? actual.map((x, i) => (i === idx ? ev.p : x)) : [...actual, ev.p];
  const ok = await guardarProcedimientosPropios(userId, lista);
  return ok ? { ok: true, lista } : { ok: false, motivo: 'error', lista: actual };
}

export async function borrarProcedimientoPropio(userId, id) {
  const actual = await cargarProcedimientosPropios(userId);
  const lista = actual.filter((x) => x.id !== id);
  if (lista.length === actual.length) return actual;
  await guardarProcedimientosPropios(userId, lista);
  return lista;
}
